import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OwnershipLevel } from '@fitness-force/enum';
import { AppErrors, BadRequestError, NotFoundError } from '@fitness-force/models';;
import type { SMSFromMasterResponse, SMSTemplateMaster, SmsTemplateMasterResponse } from '@fitness-force/models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { throwError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SmsTemplateManagementService {
  private http = inject(HttpClient);
  private router = inject(Router);


  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  getSmsFromNumbers(tenantId: number): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_SMS_SENDER_CONFIG
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
  
  getSingleEmailFromConfig(
    tenantId: number,
    fromEmailId: number
  ): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_FROM_CONFIG_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{from_email_id}', fromEmailId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  createSmsConfig(tenantId: number, data: any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.CREATE_SMS_SENDER_CONFIG
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .post(url, data, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateFromSmsConfig(
    tenantId: number,
    email_provider_config_master: number,
    emailFromId: number,
    data: any
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_EMAIL_FROM_CONFIG
      .replace('{tenant_id}', tenantId.toString())
      .replace('{email_provider_config_master}', email_provider_config_master.toString())
      .replace('{email_from_id}', emailFromId.toString());
    return this.http
      .put(url, data, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getEmailProviderConfig(tenantId: number): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_PROVIDER_CONFIG
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
  getSmsTemplate(tenantId: number): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_SMS_TEMPLATES
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getSingleSmsTemplate(tenantId: number, templateId: number): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_SMS_TEMPLATE_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{template_id}', templateId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  createSmsTemplate(ownership:OwnershipLevel, payload:any, tenantId:number, franchiseGroupId:number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.CREATE_SMS_TEMPLATE;
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.CREATE_SMS_TEMPLATE
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.CREATE_SMS_TEMPLATE
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.CREATE_SMS_TEMPLATE
          .replace('{tenant_id}', tenantId.toString());
        break;
    }
    return this.http.post(url,payload,this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateSmsTemplate(ownership:OwnershipLevel,
    tenantId: number,
    franchiseGroupId:number,
    sms_template_master_id: number,
    data: any
  ) {

    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.UPDATE_SMS_TEMPLATE
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.UPDATE_SMS_TEMPLATE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_SMS_TEMPLATE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_SMS_TEMPLATE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
    }

    return this.http.put(url,data,this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }


  updateEmailFrom(tenantId: number, fromId: number, data: any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_EMAIL_FROM_LEGACY
      .replace('{tenant_id}', tenantId.toString())
      .replace('{from_id}', fromId.toString());
    return this.http
      .put(url, data, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markActiveFromSms(tenantId: number, smsProvider: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_SMS_SENDER_ACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{sms_provider}', smsProvider.toString());
    return this.http
      .post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveFromSms(tenantId: number, emailProvider: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_SMS_SENDER_INACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{sms_provider}', emailProvider.toString());
    return this.http
      .post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markActiveTemplate(
    tenantId: number,
    sms_template_master_id: number
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_SMS_TEMPLATE_ACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{template_id}', sms_template_master_id.toString());
    return this.http
      .post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveTemplate(
    tenantId: number,
    sms_template_master_id: number
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_SMS_TEMPLATE_INACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{template_id}', sms_template_master_id.toString());
    return this.http
      .post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveTemplateByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number, sms_template_master_id:number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.MARK_SMS_TEMPLATE_INACTIVE
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.MARK_SMS_TEMPLATE_INACTIVE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_SMS_TEMPLATE_INACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_SMS_TEMPLATE_INACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
    }
    return this.http.post(url, {}, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  markActiveTemplateByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number, sms_template_master_id:number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.MARK_SMS_TEMPLATE_ACTIVE
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.MARK_SMS_TEMPLATE_ACTIVE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_SMS_TEMPLATE_ACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_SMS_TEMPLATE_ACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', sms_template_master_id.toString());
        break;
    }
    return this.http.post(url, {}, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  } 

  getSendSMSFromListByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number): Observable<SMSFromMasterResponse> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_SMS_SENDER_CONFIGS;
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_SMS_SENDER_CONFIGS
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_SMS_SENDER_CONFIG
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getSmsTemplatesByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number): Observable<SmsTemplateMasterResponse> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_SMS_TEMPLATES;
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_SMS_TEMPLATES
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_SMS_TEMPLATES
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_SMS_TEMPLATES
          .replace('{tenant_id}', tenantId.toString());
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getSMSTemplateByIdWithOwnership(ownership:OwnershipLevel, templateId:number,franchiseGroupId:number,tenantId:number): Observable<SMSTemplateMaster> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_SMS_TEMPLATE_BY_ID
          .replace('{template_id}', templateId.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_SMS_TEMPLATE_BY_ID
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', templateId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_SMS_TEMPLATE_BY_ID
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', templateId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_SMS_TEMPLATE_BY_ID
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', templateId.toString());
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }
}
